import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import getFarmSubdivisionById from '../../services/farmMapping/getFarmSubdivisionById'
import getCarbonInstances, {
  type CarbonInstancesQuery,
} from '../../services/ruutsApi/getCarbonInstances'
import { CarbonInstance, ToGeoJSON } from '../../types/apiModels'
import { queryKeys } from './queryKeys'

export type { CarbonInstancesQuery }

const toGeoJSONFeatures = (carbonInstances: CarbonInstance[]): ToGeoJSON[] =>
  carbonInstances
    .map(instance => instance.toGeoJSON)
    .filter((geojson): geojson is ToGeoJSON => !!geojson)

export const useCarbonInstances = ({
  farmId,
  yearId,
  query,
}: {
  farmId: string | undefined
  yearId?: string
  query?: CarbonInstancesQuery
}) => {
  const queryKey = queryKeys.carbonInstances(farmId, yearId ? { ...query, yearId } : query)

  const result = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      let effectiveQuery = query
      if (yearId) {
        // yearId is the subdivision's id, not a calendar year: the read needs the year it carries.
        const farmSubdivision = await getFarmSubdivisionById({
          id: yearId,
          farmId: farmId as string,
          signal,
        })
        effectiveQuery = { ...query, instanceYear: farmSubdivision.year }
      }
      return getCarbonInstances({ farmId: farmId as string, query: effectiveQuery, signal })
    },
    enabled: !!farmId,
    select: toGeoJSONFeatures,
  })

  // Draft-edit state: the live read is the source of truth until the user touches it (draw/edit/
  // clear in ShowFolders), after which the local draft owns the layer until saveFarmMapping (PR 11).
  // Reset to "untouched" only when the query identity changes (farm/year/query) — a same-key
  // background refetch (reconnect, cross-domain invalidate) must NOT clobber in-progress edits.
  // `undefined` = untouched (fall back to the live read, no empty-layer frame); `null` = cleared.
  const [draft, setDraft] = useState<ToGeoJSON[] | null | undefined>(undefined)
  const queryIdentity = JSON.stringify(queryKey)
  useEffect(() => {
    setDraft(undefined)
  }, [queryIdentity])

  const carbonInstances = draft === undefined ? (result.data ?? null) : draft

  return {
    carbonInstances,
    setCarbonInstances: setDraft,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
  }
}
